import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  IconButton,
  Tab,
  Tabs,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from '../../utils/i18n-utils';
import { format } from 'date-fns';
import * as cashManagementApi from '../../api/cashManagement';

// Define interfaces
interface Denomination extends cashManagementApi.Denomination {
  // Inherits from the API interface
}

interface CashCountData extends cashManagementApi.CashCount {
  // Inherits from the API interface
}

const CashCount: React.FC = () => {
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [countDate, setCountDate] = useState<Date | null>(new Date());
  const [expectedAmount, setExpectedAmount] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [countedBy, setCountedBy] = useState<string>('');
  const [showCompleteDialog, setShowCompleteDialog] = useState<boolean>(false);
  const [cashCountHistory, setCashCountHistory] = useState<CashCountData[]>([]);
  const [historyStartDate, setHistoryStartDate] = useState<Date | null>(null);
  const [historyEndDate, setHistoryEndDate] = useState<Date | null>(null);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Define denominations for Libyan Dinar
  const [denominations, setDenominations] = useState<Denomination[]>([
    { id: 'note-50', name: t('cashCount.denominations.note50') || 'د.ل50 Notes', value: 50, count: 0, total: 0 },
    { id: 'note-20', name: t('cashCount.denominations.note20') || 'د.ل20 Notes', value: 20, count: 0, total: 0 },
    { id: 'note-10', name: t('cashCount.denominations.note10') || 'د.ل10 Notes', value: 10, count: 0, total: 0 },
    { id: 'note-5', name: t('cashCount.denominations.note5') || 'د.ل5 Notes', value: 5, count: 0, total: 0 },
    { id: 'note-1', name: t('cashCount.denominations.note1') || 'د.ل1 Notes', value: 1, count: 0, total: 0 },
    { id: 'coin-500', name: t('cashCount.denominations.coin500') || '500 Dirhams', value: 0.5, count: 0, total: 0 },
    { id: 'coin-250', name: t('cashCount.denominations.coin250') || '250 Dirhams', value: 0.25, count: 0, total: 0 }
  ]);

  useEffect(() => {
    // Load data from the API
    const loadData = async () => {
      setLoading(true);
      try {
        // Get current cash balance from the API
        const balance = await cashManagementApi.getCurrentCashBalance();
        setExpectedAmount(balance.safeBalance);
        setLoading(false);
      } catch (error) {
        console.error('Error loading cash count data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (tabValue === 1) {
      fetchCashCountHistory();
    }
  }, [tabValue, historyStartDate, historyEndDate]);

  const fetchCashCountHistory = async () => {
    try {
      setHistoryLoading(true);
      const startDate = historyStartDate ? format(historyStartDate, 'yyyy-MM-dd') : undefined;
      const endDate = historyEndDate ? format(historyEndDate, 'yyyy-MM-dd') : undefined;
      const data = await cashManagementApi.getCashCounts(startDate, endDate);
      setCashCountHistory(data);
    } catch (error) {
      console.error('Error fetching cash count history:', error);
      setNotification({
        open: true,
        message: 'Failed to load cash count history',
        severity: 'error'
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDenominationChange = (id: string, count: number) => {
    const updatedDenominations = denominations.map(denom => {
      if (denom.id === id) {
        const newCount = Math.max(0, count); // Ensure count is not negative
        return {
          ...denom,
          count: newCount,
          total: denom.value * newCount
        };
      }
      return denom;
    });
    
    setDenominations(updatedDenominations);
  };

  const calculateTotalCounted = (): number => {
    return denominations.reduce((sum, denom) => sum + denom.total, 0);
  };

  const calculateDifference = (): number => {
    return calculateTotalCounted() - expectedAmount;
  };

  const handleSaveDraft = async () => {
    // Validate form
    if (!countedBy.trim()) {
      setNotification({
        open: true,
        message: 'Please enter who counted the cash',
        severity: 'error'
      });
      return;
    }

    // Prepare data for API
    const cashCountData: CashCountData = {
      date: format(countDate || new Date(), 'yyyy-MM-dd'),
      expectedAmount,
      actualAmount: calculateTotalCounted(),
      difference: calculateDifference(),
      status: 'draft',
      notes,
      denominations: [...denominations],
      countedBy
    };
    
    try {
      // Save to API
      const savedCashCount = await cashManagementApi.saveCashCount(cashCountData);
      console.log('Cash count saved:', savedCashCount);
      
      setNotification({
        open: true,
        message: 'Cash count draft saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving cash count:', error);
      setNotification({
        open: true,
        message: 'Error saving cash count',
        severity: 'error'
      });
    }
  };

  const handleShowCompleteDialog = () => {
    if (!countedBy.trim()) {
      setNotification({
        open: true,
        message: 'Please enter who counted the cash',
        severity: 'error'
      });
      return;
    }
    
    setShowCompleteDialog(true);
  };

  const handleCompleteCashCount = async () => {
    // Prepare data for API
    const cashCountData: CashCountData = {
      date: format(countDate || new Date(), 'yyyy-MM-dd'),
      expectedAmount,
      actualAmount: calculateTotalCounted(),
      difference: calculateDifference(),
      status: 'completed',
      notes,
      denominations: [...denominations],
      countedBy
    };
    
    try {
      // Save to API
      const savedCashCount = await cashManagementApi.saveCashCount(cashCountData);
      console.log('Cash count completed:', savedCashCount);
      
      setShowCompleteDialog(false);
      setNotification({
        open: true,
        message: 'Cash count completed successfully',
        severity: 'success'
      });
      
      // Reset form after successful completion
      resetForm();
    } catch (error) {
      console.error('Error completing cash count:', error);
      setShowCompleteDialog(false);
      setNotification({
        open: true,
        message: 'Error completing cash count',
        severity: 'error'
      });
    }
  };

  const handlePrint = () => {
    // In a real app, you would implement printing functionality
    window.print();
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  const resetForm = () => {
    setDenominations(denominations.map(denom => ({
      ...denom,
      count: 0,
      total: 0
    })));
    setNotes('');
    setCountedBy('');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalCounted = calculateTotalCounted();
  const difference = calculateDifference();
  const isDifferencePositive = difference > 0;
  const isDifferenceNegative = difference < 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {t('navigation.accounting.cashCount') || 'End of Day Cash Count'}
        </Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            sx={{ mr: 1 }}
          >
            {t('common.saveDraft') || 'Save Draft'}
          </Button>
          <Button 
            variant="contained" 
            color="success" 
            startIcon={<CheckIcon />}
            onClick={handleShowCompleteDialog}
            sx={{ mr: 1 }}
          >
            {t('common.complete') || 'Complete Count'}
          </Button>
          <Button 
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
          >
            {t('common.print') || 'Print'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label={t('cashCount.newCount') || 'New Cash Count'} />
          <Tab label={t('cashCount.history') || 'Cash Count History'} />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
      <Grid container spacing={3}>
        {/* Left Column - Count Info */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('cashCount.countInfo') || 'Count Information'}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <DatePicker
                  label={t('cashCount.countDate') || 'Count Date'}
                  value={countDate}
                  onChange={(date) => setCountDate(date)}
                  slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('cashCount.countedBy') || 'Counted By'}
                  value={countedBy}
                  onChange={(e) => setCountedBy(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('cashCount.expectedAmount') || 'Expected Amount'}
                  type="number"
                  value={expectedAmount}
                  onChange={(e) => setExpectedAmount(parseFloat(e.target.value) || 0)}
                  fullWidth
                  margin="normal"
                  InputProps={{
                    startAdornment: "د.ل",
                    readOnly: true
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('cashCount.notes') || 'Notes'}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Summary Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cashCount.summary') || 'Summary'}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{t('cashCount.expectedAmount') || 'Expected:'}</Typography>
                <Typography variant="body1">د.ل{expectedAmount.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">{t('cashCount.actualAmount') || 'Actual:'}</Typography>
                <Typography variant="body1">د.ل{totalCounted.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1">{t('cashCount.difference') || 'Difference:'}</Typography>
                <Typography 
                  variant="subtitle1"
                  color={isDifferencePositive ? 'success.main' : isDifferenceNegative ? 'error.main' : 'text.primary'}
                >
                  د.ل{difference.toFixed(2)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Denominations */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('cashCount.denomination') || 'Denomination'}</TableCell>
                    <TableCell align="right">{t('cashCount.value') || 'Value'}</TableCell>
                    <TableCell align="right">{t('cashCount.count') || 'Count'}</TableCell>
                    <TableCell align="right">{t('cashCount.total') || 'Total'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {denominations.map((denom) => (
                    <TableRow key={denom.id}>
                      <TableCell>{denom.name}</TableCell>
                      <TableCell align="right">د.ل{denom.value.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={denom.count}
                          onChange={(e) => handleDenominationChange(denom.id, parseInt(e.target.value, 10) || 0)}
                          size="small"
                          InputProps={{ inputProps: { min: 0, style: { textAlign: 'right' } } }}
                          sx={{ width: '100px' }}
                        />
                      </TableCell>
                      <TableCell align="right">د.ل{denom.total.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="subtitle1">{t('cashCount.grandTotal') || 'Grand Total:'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1">د.ل{totalCounted.toFixed(2)}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
        </>
      )}

      {tabValue === 1 && (
        <>
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <DatePicker
              label={t('cashCount.startDate') || 'Start Date'}
              value={historyStartDate}
              onChange={(newValue) => setHistoryStartDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label={t('cashCount.endDate') || 'End Date'}
              value={historyEndDate}
              onChange={(newValue) => setHistoryEndDate(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Button variant="outlined" onClick={fetchCashCountHistory} disabled={historyLoading}>
              {t('common.filter') || 'Filter'}
            </Button>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('cashCount.date') || 'Date'}</TableCell>
                    <TableCell align="right">{t('cashCount.expected') || 'Expected'}</TableCell>
                    <TableCell align="right">{t('cashCount.actual') || 'Actual'}</TableCell>
                    <TableCell align="right">{t('cashCount.difference') || 'Difference'}</TableCell>
                    <TableCell>{t('cashCount.status') || 'Status'}</TableCell>
                    <TableCell>{t('cashCount.countedBy') || 'Counted By'}</TableCell>
                    <TableCell>{t('cashCount.notes') || 'Notes'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cashCountHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {t('cashCount.noHistory') || 'No cash count history found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    cashCountHistory.map((count) => (
                      <TableRow key={count.id}>
                        <TableCell>{format(new Date(count.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell align="right">د.ل{count.expectedAmount.toFixed(2)}</TableCell>
                        <TableCell align="right">د.ل{count.actualAmount.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            color={count.difference > 0 ? 'success.main' : count.difference < 0 ? 'error.main' : 'text.primary'}
                          >
                            {count.difference > 0 ? '+' : ''}د.ل{count.difference.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={count.status === 'completed' ? (t('common.completed') || 'Completed') : (t('common.draft') || 'Draft')}
                            color={count.status === 'completed' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{count.countedBy || '-'}</TableCell>
                        <TableCell>{count.notes || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Complete Count Dialog */}
      <Dialog open={showCompleteDialog} onClose={() => setShowCompleteDialog(false)}>
        <DialogTitle>
          {t('cashCount.completeCount') || 'Complete Cash Count'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('cashCount.completeConfirmation') || 'Are you sure you want to complete this cash count?'}
          </Typography>
          
          {difference !== 0 && (
            <Alert severity={isDifferencePositive ? 'info' : 'warning'} sx={{ mb: 2 }}>
              {isDifferencePositive
                ? t('cashCount.overageWarning', { amount: Math.abs(difference).toFixed(2) }) || `There is an overage of د.ل${Math.abs(difference).toFixed(2)}`
                : t('cashCount.shortageWarning', { amount: Math.abs(difference).toFixed(2) }) || `There is a shortage of د.ل${Math.abs(difference).toFixed(2)}`}
            </Alert>
          )}
          
          <Typography variant="body2">
            {t('cashCount.completeNote') || 'Once completed, this cash count cannot be modified.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteDialog(false)} color="secondary">
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleCompleteCashCount} color="primary" variant="contained">
            {t('common.confirm') || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashCount; 